/* Contextual help, one panel per screen — ported from the console.
 *
 * The console already answers the question "what am I looking at?" on
 * every screen (`console-frontend/src/components/HelpPanel.tsx`, 109
 * lines, the pattern Pablo already signed off on): a trigger opens a
 * side rail with a short body, a list of pointers into the tutorial and
 * a button that goes there. Plan J's D.2 asks for the same thing in the
 * builder, and inventing a second help idiom in the same product would
 * be worse than either one alone. So this is the console's panel, with
 * three deliberate differences, each of which is a real difference
 * between the two apps and not a preference:
 *
 *   1. The builder's guide is a ROUTE (`/guide`), not a modal, so the
 *      footer button navigates instead of calling `onOpenTutorial`.
 *   2. The console's pages receive `onHelp` as a prop from `App.tsx`,
 *      which renders each page by hand. The builder renders pages
 *      through `<Outlet />` and cannot pass anything down, so the
 *      TRIGGER lives in the shell — see `SCREEN_HELP_ROUTES` and
 *      `topicForLocation` below, and `AppShell.tsx` for the wiring.
 *   3. The chrome strings live under `screenhelp.` rather than the
 *      console's `common.helpForThisScreen` / `help.contents` /
 *      `help.reopen`, because `.github/scripts/check_guide_keys.py`
 *      scans that prefix and a group the gate cannot see is a group
 *      that ships half-translated — this app's i18next carries
 *      `fallbackLng: "en"`, so a missing ES key renders ENGLISH at a
 *      Spanish operator with nothing anywhere saying so.
 *
 * WHY THERE IS STILL NO `<ScreenHelpButton>` HERE
 * -----------------------------------------------
 * J2's note said a per-page button became a five-line addition "when J3
 * lands /admin/access/* as real pages that own their headers". They
 * landed, and the button is still not here — a decision, not an
 * oversight. The seven Accesos routes are all reached from the sidebar,
 * the shell's AYUDA entry appears on every one of them (its appearing
 * IS the signal that help exists), and a second trigger in each page
 * header would be the same affordance twice, in two idioms, with two
 * places to forget one. If a screen ever owns help the sidebar cannot
 * reach — a modal, a wizard step — that is when the button earns its
 * export; `SCREEN_HELP_ROUTES` keeps working underneath it either way.
 */

import { Button, SidePanel } from "@omnitok/ui";
import { BookOpen } from "lucide-react";
import type { ReactNode } from "react";

import { useT } from "@/i18n";

/**
 * One member per screen that has help.
 *
 * J2 predicted J3 would add four — "scopes" | "groups" | "people" |
 * "audit". It adds SEVEN, and the extra three are the reason this
 * comment is worth reading. Each of the three lists has a DETAIL route
 * of its own (`/admin/access/scopes/:scopeId` and friends), assertion F
 * demands a topic for every live `/admin/*` route, and no new exemption
 * is buyable because `GRANDFATHER_CAP` only moves down. So the detail
 * routes needed an answer.
 *
 * Pointing them at their list's topic would have PASSED the gate —
 * `mapped` is a list of pairs with no uniqueness rule on the topic — and
 * it is exactly what this file's own note about Dimensiones calls out:
 * "pretending otherwise … would offer 'what is this screen?' on the
 * Reportes tab and answer about a different one". A list and a detail
 * are two screens that answer two questions. The `query` escape does not
 * help either: it matches exact VALUES (`params.get(k) === v`), so a
 * scope id cannot be discriminated by it.
 *
 * One topic per DETAIL screen, not per tab: `group` covers Miembros ·
 * Ámbito · Contenido together, because five topics for one screen is
 * copy nobody reads.
 *
 * J4 TOOK THE ESCAPE THIS NOTE OFFERED, for the two tabs it adds and
 * for no others. The line above used to end "If J4's AI and Experiencia
 * tabs want their own, `query`-discriminated entries are a pure
 * addition", and they do want their own — not for symmetry, but because
 * the three J3 tabs answer *who may read what* while these two answer
 * *what the product says and does to them once they may*. A reader on
 * Experiencia asking "what is this screen?" and being told about the
 * content matrix has been answered about a different question. The two
 * new topics carry the two facts that are genuinely hard to guess from
 * the form: the AI toggle is a COST that is off until somebody turns it
 * on, and a board template decides what a member STARTS with and never
 * what they may see.
 *
 * J5 adds the portal's Personas in its OWN app and its OWN dictionary,
 * which this file cannot see (see the gate's docstring).
 */
export type HelpTopic =
  | "dimensions"
  | "scopes"
  | "scope"
  | "groups"
  | "group"
  | "groupAi"
  | "groupExperience"
  | "people"
  | "person"
  | "audit"
  | "cache";

/**
 * topic → its two dictionary keys. The keys are spelled out as LITERAL
 * strings, not built from the topic name, so the existing vitest
 * `i18n-keys.test.ts` sees them: it matches on a double-quoted literal
 * inside `t(`, so a `t(keys.title)` call site is invisible to it. Belt;
 * the new CI gate is the braces.
 */
export const TOPIC_KEYS: Record<HelpTopic, { title: string; body: string }> = {
  dimensions: {
    title: "screenhelp.dimensions.title",
    body: "screenhelp.dimensions.body",
  },
  scopes: {
    title: "screenhelp.scopes.title",
    body: "screenhelp.scopes.body",
  },
  scope: {
    title: "screenhelp.scope.title",
    body: "screenhelp.scope.body",
  },
  groups: {
    title: "screenhelp.groups.title",
    body: "screenhelp.groups.body",
  },
  group: {
    title: "screenhelp.group.title",
    body: "screenhelp.group.body",
  },
  groupAi: {
    title: "screenhelp.groupAi.title",
    body: "screenhelp.groupAi.body",
  },
  groupExperience: {
    title: "screenhelp.groupExperience.title",
    body: "screenhelp.groupExperience.body",
  },
  people: {
    title: "screenhelp.people.title",
    body: "screenhelp.people.body",
  },
  person: {
    title: "screenhelp.person.title",
    body: "screenhelp.person.body",
  },
  audit: {
    title: "screenhelp.audit.title",
    body: "screenhelp.audit.body",
  },
  cache: {
    title: "screenhelp.cache.title",
    body: "screenhelp.cache.body",
  },
};

/** Pointers into the Guía, per screen. Rendered as the "Contents" list. */
export const TOPIC_READMORE: Record<HelpTopic, string[]> = {
  dimensions: ["guide.dimensions.title", "guide.data.title"],
  // The scope chapter first, then the dimension one: a scope's rules
  // name dimension keys, and the state an operator most often has to
  // explain on this screen ("no aplica") is a fact about the DIMENSION.
  scopes: ["guide.scopes.title", "guide.dimensions.title"],
  // The DETAIL gets a third pointer the list does not: this is the
  // screen where the row counts are read, and reading them against the
  // analyst's own number is step 2 of the walk.
  scope: [
    "guide.scopes.title",
    "guide.dimensions.title",
    "guide.verify.title",
  ],
  groups: ["guide.groups.title", "guide.scopes.title"],
  group: ["guide.groups.title", "guide.scopes.title"],
  // The AI tab's nearest chapter is the AI one — training examples,
  // and why a restricted chat is deliberately duller — and then the
  // walk, which is where the group's own narrative is checked before
  // anybody reads it.
  groupAi: ["guide.ai.title", "guide.verify.title"],
  groupExperience: ["guide.groups.title", "guide.verify.title"],
  people: ["guide.people.title", "guide.groups.title"],
  // The walk belongs on a PERSON and on a SCOPE, which are the two
  // screens somebody stands on while asking "is this right yet?".
  person: ["guide.people.title", "guide.verify.title"],
  audit: ["guide.people.title", "guide.groups.title"],
  // A warm runs inside each reload and is published with it.
  cache: ["guide.refresh.title", "guide.publish.title"],
};

/**
 * Which screen gets which topic, resolved by the shell.
 *
 * `path` is spelled EXACTLY as in `App.tsx` — assertion G of
 * `check_guide_keys.py` fails if it stops being a real route, so a
 * renamed route cannot leave a dead entry behind. `query` is the extra
 * discriminator a screen needs when it is a sub-view rather than a
 * route of its own: every listed param must match for the topic to
 * apply. Dimensiones is exactly that case (`?tab=data&data=dimensions`),
 * and pretending otherwise — mapping the bare `/projects/:slug` to it —
 * would offer "what is this screen?" on the Reportes tab and answer
 * about a different one.
 */
export const SCREEN_HELP_ROUTES: ReadonlyArray<{
  path: string;
  query?: Record<string, string>;
  topic: HelpTopic;
}> = [
  {
    path: "/projects/:slug",
    query: { tab: "data", data: "dimensions" },
    topic: "dimensions",
  },
  // Plan J J3 — the seven Accesos routes, none of which needs a
  // `query`: they are real routes, not sub-views, which is the whole
  // reason `AccessTabs` is a component of <Link>s and not a layout
  // route. (J4 adds two entries that DO carry one, three lines down,
  // and they are the exception that proves the shape: a TAB inside the
  // group screen is a sub-view, and the URL is where it says so.)
  { path: "/admin/access/scopes", topic: "scopes" },
  { path: "/admin/access/scopes/:scopeId", topic: "scope" },
  { path: "/admin/access/groups", topic: "groups" },
  // Plan J J4 — the two new tabs of the group screen, and THE ORDER IS
  // LOAD-BEARING: `topicForLocation` returns the FIRST entry whose path
  // matches and whose every `query` pair matches, and the entry below
  // carries no `query`, so it matches every tab. Written after these
  // two it would answer for them.
  //
  // The `tab` VALUES are the ones `GroupDetailPage` puts in the URL —
  // read from its `GroupTab` union and its `readTab`, not guessed:
  // `"members" | "scope" | "content" | "ai" | "experience"`, with
  // `setSearchParams({ tab: id })` for every tab but the first.
  //
  // That coupling is the one thing here CI does not check: assertion G
  // of `check_guide_keys.py` proves the PATH is a real route, and
  // nothing proves the query VALUE is a real tab — a rename would just
  // stop offering help on that tab, in silence. `readTab` is exported,
  // so pinning the two lists against each other is a cheap test for
  // whoever adds the third one; until then, rename in the same commit.
  {
    path: "/admin/access/groups/:groupId",
    query: { tab: "ai" },
    topic: "groupAi",
  },
  {
    path: "/admin/access/groups/:groupId",
    query: { tab: "experience" },
    topic: "groupExperience",
  },
  { path: "/admin/access/groups/:groupId", topic: "group" },
  { path: "/admin/access/people", topic: "people" },
  { path: "/admin/access/people/:userId", topic: "person" },
  { path: "/admin/access/audit", topic: "audit" },
  { path: "/admin/cache", topic: "cache" },
];

/**
 * Does `pathname` match a route pattern? `:param` matches exactly one
 * non-empty segment; everything else is literal. Deliberately tiny —
 * react-router's own matcher is not reachable outside a `<Routes>`
 * subtree and the patterns here have no splats or optional segments.
 */
function pathMatches(pattern: string, pathname: string): boolean {
  const pat = pattern.split("/");
  const got = pathname.replace(/\/+$/, "").split("/");
  if (pat.length !== got.length) return false;
  return pat.every((seg, i) =>
    seg.startsWith(":") ? (got[i] ?? "") !== "" : seg === got[i],
  );
}

/** The topic for the screen at `pathname` + `search`, or null. */
export function topicForLocation(
  pathname: string,
  search: string,
): HelpTopic | null {
  const params = new URLSearchParams(search);
  for (const entry of SCREEN_HELP_ROUTES) {
    if (!pathMatches(entry.path, pathname)) continue;
    const q = entry.query ?? {};
    if (Object.entries(q).every(([k, v]) => params.get(k) === v)) {
      return entry.topic;
    }
  }
  return null;
}

/**
 * The rail itself. Lives in the Layout's `rightPanel`; closing it is
 * the parent dropping it, which is what `SidePanelProps.onClose`
 * documents.
 */
export function ScreenHelpPanel({
  topic,
  onClose,
  onOpenGuide,
}: {
  topic: HelpTopic;
  onClose: () => void;
  onOpenGuide: () => void;
}): ReactNode {
  const { t } = useT();
  const keys = TOPIC_KEYS[topic];
  return (
    <SidePanel
      title={t(keys.title)}
      description={t("screenhelp.forThisScreen")}
      onClose={onClose}
      footer={
        <Button
          variant="primary"
          fullWidth
          leftIcon={<BookOpen size={16} />}
          onClick={onOpenGuide}
        >
          {t("screenhelp.openGuide")}
        </Button>
      }
    >
      <div className="space-y-4">
        <p className="text-sm leading-relaxed text-neutral-700">
          {t(keys.body)}
        </p>
        <div>
          <p className="mb-2 text-xs font-semibold text-neutral-500">
            {t("screenhelp.contents")}
          </p>
          <ul className="space-y-1.5">
            {TOPIC_READMORE[topic].map((key) => (
              <li key={key} className="text-sm text-neutral-600">
                <button
                  type="button"
                  className="text-left underline decoration-dotted underline-offset-4 hover:text-primary"
                  onClick={onOpenGuide}
                >
                  {t(key)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </SidePanel>
  );
}
